/**
 * O cartão da ligação privada — a tocar, a chamar, em ligação, e o aviso do fim.
 *
 * Vive na app e não no escritório, de propósito: o escritório desmonta quando se
 * muda para o modo Discord, e uma chamada não pode cair por se ir ver o chat. É
 * também aqui que a ligação ganha voz — ao ser atendida entra-se na sala do
 * LiveKit dela (e sai-se da call de sala, se se estava numa); ao acabar, sai-se.
 * O escritório, do lado dele, deixa de puxar as pessoas para as calls das salas
 * enquanto a ligação dura (ver `onRoomChange` em `OfficeSpace`).
 *
 * Os sons: quem RECEBE ouve o toque em repetição; quem LIGA ouve o "a chamar";
 * atender e desligar têm cada um o seu. Nada disto toca a quem não está na
 * ligação — o servidor só manda os eventos às duas pontas.
 */
import type { GatewayEvent } from "@greenvoice/shared";
import { useEffect, useRef, useState } from "react";

import { connectMuted, useVoiceActions, useVoiceSnapshot } from "../hooks/useVoice";
import { api } from "../lib/api";
import { playSound, tocarEmRepeticao } from "../lib/sounds";
import { voiceEngine } from "../lib/voice";
import { gatewayClient, useStore } from "../store/store";

import { IconClose, IconMic, IconMicOff } from "./Icons";

type Motivo = Extract<GatewayEvent, { type: "LigacaoTerminada" }>["motivo"];

/** O texto do aviso de fim, do ponto de vista de quem o lê. */
export function textoDoFim(motivo: Motivo, nome: string, liguei: boolean): string {
  switch (motivo) {
    case "recusada":
      return liguei ? `${nome} recusou a chamada` : "Chamada recusada";
    case "cancelada":
      return liguei ? "Chamada cancelada" : `Chamada perdida de ${nome}`;
    case "sem_resposta":
      return liguei ? `${nome} não atendeu` : `Chamada perdida de ${nome}`;
    case "ocupado":
      return `${nome} está noutra ligação`;
    case "nao_incomodar":
      return `${nome} está em Não incomodar`;
    case "saiu":
      return `A ligação com ${nome} caiu`;
    case "indisponivel":
      return "Não foi possível ligar";
    default:
      return "Ligação terminada";
  }
}

/** `mm:ss` (ou `h:mm:ss`) de uma duração em milissegundos. */
export function duracao(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** Um auscultador, visto de lado. `desligar` roda-o para baixo, como nos telefones. */
function Telefone({ desligar = false }: { desligar?: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      style={desligar ? { transform: "rotate(135deg)" } : undefined}
    >
      <path d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25 11.4 11.4 0 0 0 3.6.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1z" />
    </svg>
  );
}

export function LigacaoPainel() {
  const ligacao = useStore((s) => s.ligacao);
  const fim = useStore((s) => s.ligacaoFim);
  const limparFim = useStore((s) => s.limparFimDeLigacao);
  const outroId = ligacao?.outro ?? fim?.outro ?? null;
  const outro = useStore((s) => (outroId ? s.users[outroId] : undefined));
  const voiceActions = useVoiceActions();
  const snapshot = useVoiceSnapshot();
  const [erro, setErro] = useState<string | null>(null);
  const [agora, setAgora] = useState(() => Date.now());

  const idLigacao = ligacao?.id ?? null;
  const estado = ligacao?.estado ?? null;
  const liguei = ligacao?.liguei ?? false;
  const spaceId = ligacao?.spaceId ?? null;

  // ── Os sons de quem está à espera: o toque (recebo) e o "a chamar" (liguei).
  useEffect(() => {
    if (!idLigacao || estado !== "a_tocar") return;
    return liguei ? tocarEmRepeticao("chamando", 3000) : tocarEmRepeticao("toque", 2600);
  }, [idLigacao, estado, liguei]);

  // ── O fim: o som, e o aviso some sozinho ao fim de uns segundos.
  useEffect(() => {
    if (!fim) return;
    playSound("fimLigacao");
    const t = window.setTimeout(limparFim, 4500);
    return () => window.clearTimeout(t);
  }, [fim, limparFim]);

  // ── O relógio da ligação em curso.
  useEffect(() => {
    if (estado !== "em_curso") return;
    const t = window.setInterval(() => setAgora(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [estado]);

  // ── A voz: atendida → entra-se na sala da ligação; acabou → sai-se dela.
  const ligadoA = useRef<string | null>(null);
  useEffect(() => {
    if (estado === "em_curso" && idLigacao && spaceId) {
      if (ligadoA.current === idLigacao) return;
      ligadoA.current = idLigacao;
      setErro(null);
      playSound("atendida");
      void (async () => {
        try {
          // Uma chamada de cada vez: quem estava numa call de sala sai dela.
          if (useStore.getState().voice) await voiceActions.leave();
          const result = await api.voice.joinLigacao(spaceId, idLigacao);
          if (useStore.getState().ligacao?.id !== idLigacao) return; // desligaram entretanto
          // Ao contrário das calls de sala, entra-se A FALAR: atender é o gesto de
          // quem quer conversar, e uma ligação muda à partida só confunde.
          await voiceEngine.connect({ ...connectMuted(result), micEnabled: result.capabilities.canSpeak });
        } catch {
          setErro("Não foi possível ligar a voz desta chamada.");
        }
      })();
      return;
    }
    if (estado === null && ligadoA.current !== null) {
      ligadoA.current = null;
      // Só se desliga o motor se não houver uma call de sala a usá-lo.
      if (useStore.getState().voice === null) void voiceEngine.disconnect();
    }
  }, [estado, idLigacao, spaceId, voiceActions]);

  if (!ligacao && !fim) return null;

  const nome = outro ? (outro.displayName ?? outro.username) : "alguém";
  const inicial = nome.trim().charAt(0).toUpperCase() || "?";

  if (!ligacao && fim) {
    return (
      <div className="ligacao ligacao--fim" role="status" aria-live="polite">
        <span className="ligacao__avatar">{inicial}</span>
        <div className="ligacao__texto">
          <strong>{textoDoFim(fim.motivo, nome, fim.liguei)}</strong>
        </div>
        <button type="button" className="ligacao__fechar" onClick={limparFim} aria-label="Fechar aviso">
          <IconClose size={16} />
        </button>
      </div>
    );
  }
  if (!ligacao) return null;

  const gw = gatewayClient();

  if (ligacao.estado === "a_tocar" && !ligacao.liguei) {
    return (
      <div className="ligacao ligacao--recebo" role="alertdialog" aria-label={`${nome} está a ligar`}>
        <span className="ligacao__avatar">{inicial}</span>
        <div className="ligacao__texto">
          <strong>{nome}</strong>
          <span>está a ligar-te…</span>
        </div>
        <div className="ligacao__botoes">
          <button
            type="button"
            className="ligacao__botao ligacao__botao--recusar"
            onClick={() => gw?.atenderLigacao(ligacao.id, false)}
            aria-label="Recusar"
            title="Recusar"
          >
            <Telefone desligar />
          </button>
          <button
            type="button"
            className="ligacao__botao ligacao__botao--atender"
            onClick={() => gw?.atenderLigacao(ligacao.id, true)}
            aria-label="Atender"
            title="Atender"
          >
            <Telefone />
          </button>
        </div>
      </div>
    );
  }

  if (ligacao.estado === "a_tocar") {
    return (
      <div className="ligacao ligacao--ligo" role="status" aria-live="polite">
        <span className="ligacao__avatar">{inicial}</span>
        <div className="ligacao__texto">
          <strong>{nome}</strong>
          <span>A chamar…</span>
        </div>
        <div className="ligacao__botoes">
          <button
            type="button"
            className="ligacao__botao ligacao__botao--recusar"
            onClick={() => gw?.desligarLigacao(ligacao.id)}
            aria-label="Cancelar chamada"
            title="Cancelar"
          >
            <Telefone desligar />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ligacao ligacao--curso" role="status">
      <span className="ligacao__avatar">{inicial}</span>
      <div className="ligacao__texto">
        <strong>{nome}</strong>
        <span>{erro ?? `Em ligação · ${duracao(agora - ligacao.desde)}`}</span>
      </div>
      <div className="ligacao__botoes">
        <button
          type="button"
          className={`ligacao__botao ligacao__botao--mic${snapshot.micEnabled ? "" : " is-off"}`}
          onClick={() => void voiceActions.toggleMic()}
          aria-label={snapshot.micEnabled ? "Silenciar microfone" : "Ligar microfone"}
          title={snapshot.micEnabled ? "Silenciar" : "Ligar microfone"}
        >
          {snapshot.micEnabled ? <IconMic size={18} /> : <IconMicOff size={18} />}
        </button>
        <button
          type="button"
          className="ligacao__botao ligacao__botao--recusar"
          onClick={() => gw?.desligarLigacao(ligacao.id)}
          aria-label="Desligar"
          title="Desligar"
        >
          <Telefone desligar />
        </button>
      </div>
    </div>
  );
}
